import * as React from 'react';
import {weakMemo} from '@packages/utilities/memo';
import {TypeImplementation, Expressions} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAll,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {useSharedFlowContext, LogicLabel} from '@packages/systems/flows/shared';
import {
  getCollectionNameFromVariable,
  selectCMSCollections,
  getIcon,
  isSupportedField,
  initializeField,
  getFieldBySlug,
  BASIC_FIELDS,
  getBasicSlugs,
  getCustomSlugs,
} from '../utils';
import {
  ExpressionEditor,
  updateExpressionAtAction,
  extendExpressionEditorActionAtPath,
} from '@packages/systems/ExpressionEditor-utils';
import {
  EUnion,
  ECall,
  ERecord,
  TTypeReference,
  EHole,
  EList,
  type Expression,
} from '@packages/systems/wfdl-ast';
import {CollectionSettings} from '../components/CollectionSettings';
import {confirm} from '@packages/systems/dialog';
import {a} from '@packages/utilities/wfdl-helpers';
import {TEffect, StyledHelpIcon} from '@packages/systems/plugins/Effect';
import {EnhancedDropdownWithNote} from '@packages/systems/design-system/Dropdown';
import SelectButton from '@packages/systems/design-system/SelectButton';
import {
  styledDiv,
  styled,
  emotionStyled,
  tokens,
} from '@packages/utilities/styled';
import {Subheading} from '@packages/systems/design-system/Text';
import {Note} from '@packages/systems/spring';
import TooltipControl from '@packages/systems/design-system/TooltipControl';
import {type CollectionRecordType} from '@packages/systems/dynamo/records/CollectionRecord';
import {CheckMedium as Checkmark} from '@packages/systems/ui/icons';
import {useDesignerSelector} from '@packages/systems/designer/flux';

const getUnionData = (expr: ContextualExpression) => {
  const union = ContextualExpressions.getUnion(stripAnn(expr));
  const val = union && union.value;
  const tag = union && union.tag;
  return {val, tag};
};

/**
 * WFDL types
 */
const error = TTypeReference('error');
const itemUpdatersType = TEffect(error, a);

/**
 * Constants
 */
const ADD_ID_MANUALLY = 'Add item ID';
const ENHANCED_DROPDOWN_WIDTH = 220;
const StyledFieldHeader = styledDiv(({theme: {gutters}}) => ({
  display: 'flex',
  alignItems: 'center',
  columnGap: gutters.x4,
  position: 'relative',
  top: -2,
}));
const noteText =
  'Add a new value for each field you want to update. Any field left blank will be empty.';
const toggleSelectId = '_button-toggle-select-all';
const toggleDeselectId = '_button-toggle-deselect-all';

/**
 * Get labels
 */

// Get labels for the select menu
const getLabelForSelect = ({
  fieldSlug,
  selectedSlugs,
  collection,
}: {
  fieldSlug: string;
  selectedSlugs: Array<string>;
  collection: CollectionRecordType;
}) => {
  const field = getFieldBySlug({fieldSlug, collection});
  const isSelected = selectedSlugs.includes(fieldSlug);
  const Icon = getIcon(field);
  return (
    <StyledOption>
      {isSelected ? <StyledCheckmark /> : <Space />} {Icon && <Icon />}{' '}
      {field.name}
    </StyledOption>
  );
};

/**
 * Expression path parts
 */
const atArg = {in: 'Call', at: 'arg'} as const;
const atItemID = {in: 'Record', at: 'Item ID'} as const;
const atItemStatus = {in: 'Record', at: 'Item Status'} as const;
const atSelectedFields = {in: 'Record', at: 'Selected Fields'} as const;
const atListIndex = (idx: number) => ({in: 'List', index: idx});
const atUnionTag = (tag: string) => ({in: 'Union', tag});

/**
 * Dispatchers
 */
const dispatchItemID = weakMemo(
  (dispatch: TypeImplementation.ExpressionEditorDispatch) =>
    extendExpressionEditorActionAtPath(dispatch)([atArg, atItemID])
);
const dispatchItemStatus = weakMemo(
  (dispatch: TypeImplementation.ExpressionEditorDispatch) =>
    extendExpressionEditorActionAtPath(dispatch)([atArg, atItemStatus])
);
const dispatchSelectedFields = ({
  listIndex,
  tag,
}: {
  listIndex: number;
  tag: string;
}) => {
  return (dispatch: TypeImplementation.ExpressionEditorDispatch) =>
    extendExpressionEditorActionAtPath(dispatch)([
      atArg,
      atSelectedFields,
      // @ts-expect-error - TS2322 - Type '{ in: string; index: number; }' is not assignable to type 'ExpressionPathPart'.
      atListIndex(listIndex),
      // @ts-expect-error - TS2322 - Type '{ in: string; tag: string; }' is not assignable to type 'ExpressionPathPart'.
      atUnionTag(tag),
    ]);
};

/**
 * Utils for getting, setting, and sorting the list of selected fields
 */

const removeFieldFromList = (
  fieldSlug: string,
  list: Array<ContextualExpression>
) => {
  const newList = list.reduce<Array<any>>((acc, item) => {
    const {tag} = getUnionData(item);
    if (tag !== fieldSlug) {
      acc.push(stripAll(item));
      return acc;
    }
    return acc;
  }, []);
  return newList;
};

const addFieldToList = ({
  fieldSlug,
  collection,
  list,
}: {
  fieldSlug: string;
  collection: CollectionRecordType;
  list: Array<ContextualExpression>;
}) => {
  const field = getFieldBySlug({fieldSlug, collection});
  const strippedList = list.map(stripAll);
  const union = EUnion({tag: fieldSlug, value: initializeField(field)});
  const newList = strippedList.concat([union]);
  return newList;
};

// Not a UI selector:
// eslint-disable-next-line webflow/selector-memoization
const selectAll = ({
  selectedFieldExpressions,
  collection,
  allFieldSlugs,
  selectedSlugs,
}: {
  selectedFieldExpressions: Array<ContextualExpression>;
  collection: CollectionRecordType;
  allFieldSlugs: Array<string>;
  selectedSlugs: Array<string>;
}) => {
  const strippedList = selectedFieldExpressions.map(stripAll);
  return allFieldSlugs.reduce((acc, fieldSlug) => {
    if (!selectedSlugs.includes(fieldSlug)) {
      const field = getFieldBySlug({fieldSlug, collection});
      const union = EUnion({tag: fieldSlug, value: initializeField(field)});
      acc.push(union);
      return acc;
    }
    return acc;
  }, strippedList);
};

const getField = (list: Array<Expression>, slug: string) =>
  list.filter((item) => {
    const {tag} = Expressions.getUnion(item) || {};
    return tag === slug;
  });
const getCustomFields = (list: Array<Expression>) =>
  list.filter((item) => {
    const {tag} = Expressions.getUnion(item) || {};
    // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type '"name" | "slug"'.
    return !BASIC_FIELDS.includes(tag);
  });
// Sort the list of fields so that name and slug always appear first
const sortFields = (list: Array<Expression>): Array<Expression> => {
  return [
    ...getField(list, 'name'),
    ...getField(list, 'slug'),
    ...getCustomFields(list),
  ];
};

/**
 * Data for the select menu
 */
const getFieldHeader = (label: string) => (
  <StyledFieldHeader>{label}</StyledFieldHeader>
);
const basicHeader = [
  {
    id: '_header-basic-info',
    label: getFieldHeader('Basic info'),
    header: true,
    disabled: true,
  },
];

const getBasicFieldLabels = ({
  selectedCollection,
  selectedSlugs,
}: {
  selectedCollection: CollectionRecordType;
  selectedSlugs: Array<string>;
}) => {
  return selectedCollection
    ? // We'll created a label for each basic field
      BASIC_FIELDS.map((basicField) => ({
        id: basicField,
        label: getLabelForSelect({
          fieldSlug: basicField,
          selectedSlugs,
          collection: selectedCollection,
        }),
      }))
    : [];
};

const getCustomFieldLabels = ({
  selectedCollection,
  selectedSlugs,
  allFieldSlugs,
}: {
  selectedCollection: CollectionRecordType;
  selectedSlugs: Array<string>;
  allFieldSlugs: Array<string>;
}) => {
  const customSlugs = getCustomSlugs(allFieldSlugs);
  return selectedCollection
    ? customSlugs.map((f) => {
        return {
          id: f,
          label: getLabelForSelect({
            fieldSlug: f,
            selectedSlugs,
            collection: selectedCollection,
          }),
        };
      })
    : [];
};

const getCustomHeader = (hasCustomFields: boolean) => {
  return hasCustomFields
    ? [
        {
          id: '_header-custom-fields',
          label: getFieldHeader('Custom fields'),
          header: true,
          disabled: true,
        },
      ]
    : [];
};

const getToggleSelectAllButton = (allFieldsAreSelected: boolean) => {
  return [
    {
      id: `_button-toggle-${
        allFieldsAreSelected ? 'deselect-all' : 'select-all'
      }`,
      label: allFieldsAreSelected ? 'Deselect all' : 'Select all',
    },
  ];
};

/**
 * Type that updates a CMS item
 */
export const ItemUpdaters = TypeImplementation.Alias({
  params: ['error', 'a'],
  system: {bindUIVariant: 'custom'},
  type: itemUpdatersType,
  render: function ItemUpdaters({expr, dispatch}) {
    const {useCMSBlock} = useSharedFlowContext();
    const {hasDependencies} = useCMSBlock();
    const collections = useDesignerSelector(selectCMSCollections);
    const call = ContextualExpressions.getCall(stripAnn(expr));
    const collectionName = call
      ? getCollectionNameFromVariable(stripAll(call.fun))
      : null;
    const selectedCollection = React.useMemo(
      () => collections.find((c) => c.pluralName === collectionName),
      [collections, collectionName]
    );

    const itemRecord = call
      ? ContextualExpressions.getRecord(stripAnn(call.arg))
      : null;
    const selectedFieldsExpr = itemRecord && itemRecord['Selected Fields'];
    const selectedFieldsExprList = React.useMemo(
      () =>
        selectedFieldsExpr
          ? ContextualExpressions.getList(stripAnn(selectedFieldsExpr)) || []
          : [],
      [selectedFieldsExpr]
    );

    // @ts-expect-error - TS2345 - Argument of type '() => (string | null)[]' is not assignable to parameter of type '() => string[]'.
    const selectedSlugs = React.useMemo<Array<string>>(() => {
      return selectedFieldsExprList
        ? selectedFieldsExprList
            .map((item) => {
              const {tag} = getUnionData(item);
              return tag;
            })
            .filter(Boolean)
        : [];
    }, [selectedFieldsExprList]);

    const allFieldSlugs = React.useMemo<Array<string>>(() => {
      return selectedCollection
        ? selectedCollection.fields.reduce<Array<any>>((acc, field) => {
            if (isSupportedField(field)) {
              acc.push(field.slug);
            }
            return acc;
          }, [])
        : [];
    }, [selectedCollection]);

    const selectedBasicFields = React.useMemo(() => {
      return getBasicSlugs(selectedSlugs);
    }, [selectedSlugs]);

    const selectedCustomFields = React.useMemo(() => {
      return getCustomSlugs(selectedSlugs);
    }, [selectedSlugs]);

    const dropdownList = React.useMemo(() => {
      const allFieldsAreSelected =
        selectedSlugs.length === allFieldSlugs.length;
      const collectionHasCustomFields =
        getCustomSlugs(allFieldSlugs).length > 0;

      // Generate the labels for the dropdown list
      const labels = basicHeader.concat(
        // @ts-expect-error - TS2769 - No overload matches this call.
        getBasicFieldLabels({selectedCollection, selectedSlugs}),
        getCustomHeader(collectionHasCustomFields),
        getCustomFieldLabels({
          allFieldSlugs,
          selectedCollection,
          selectedSlugs,
        }),
        getToggleSelectAllButton(allFieldsAreSelected)
      );

      return labels;
    }, [allFieldSlugs, selectedCollection, selectedSlugs]);

    const onCollectionChange = React.useCallback(
      // @ts-expect-error - TS7006 - Parameter 'nextCollectionName' implicitly has an 'any' type.
      async (nextCollectionName) => {
        const coll = collections.find(
          (c) => c.pluralName === nextCollectionName
        );
        if (!coll) return;
        const newExpression = ECall({
          fun: Expressions.qualifiedVariable(
            '__SitePlugin',
            `Update ${nextCollectionName}`
          ),
          arg: ERecord({
            'Item ID': EUnion({
              tag: ADD_ID_MANUALLY,
              value: EHole(),
            }),
            'Item Status': EUnion({
              tag: 'current',
              value: ERecord({}),
            }),
            'Selected Fields': EList([]),
          }),
        });
        if (hasDependencies) {
          const response = await confirm({
            title: 'Change Collection?',
            content:
              "You're currently using data from the current collection in other parts of the flow. Changing a collection might break the flow.",
            submit: {label: 'Change Collection', intent: 'primary'},
            cancel: {label: 'Cancel', intent: 'default'},
          });
          if (response.type === 'confirm') {
            dispatch(updateExpressionAtAction(newExpression, []));
          }
        } else {
          dispatch(updateExpressionAtAction(newExpression, []));
        }
      },
      [dispatch, hasDependencies, collections]
    );

    const handleFieldSelect = React.useCallback(
      // @ts-expect-error - TS7006 - Parameter 'id' implicitly has an 'any' type.
      (id) => {
        const fieldInList = selectedSlugs.includes(id);

        // If the action is to select all fields
        if (id === toggleSelectId) {
          const selectedAllFields = selectAll({
            allFieldSlugs,
            collection: selectedCollection,
            selectedFieldExpressions: selectedFieldsExprList,
            selectedSlugs,
          });

          dispatch(
            updateExpressionAtAction(EList(sortFields(selectedAllFields)), [
              atArg,
              atSelectedFields,
            ])
          );
        }

        // Otherwise, if the action is to deselect all fields
        else if (id === toggleDeselectId) {
          dispatch(
            updateExpressionAtAction(EList([]), [atArg, atSelectedFields])
          );
        }
        // Otherwise the action is to deselect an individual field
        // or to select a new individual field
        else {
          if (fieldInList) {
            dispatch(
              updateExpressionAtAction(
                EList(
                  sortFields(removeFieldFromList(id, selectedFieldsExprList))
                ),
                [atArg, atSelectedFields]
              )
            );
          } else {
            dispatch(
              updateExpressionAtAction(
                EList(
                  sortFields(
                    addFieldToList({
                      fieldSlug: id,
                      collection: selectedCollection,
                      list: selectedFieldsExprList,
                    })
                  )
                ),
                [atArg, atSelectedFields]
              )
            );
          }
        }
      },
      [
        selectedFieldsExprList,
        dispatch,
        selectedCollection,
        selectedSlugs,
        allFieldSlugs,
      ]
    );

    return (
      <>
        <CollectionSettings
          onChange={onCollectionChange}
          value={collectionName}
          collections={collections}
          blockType="Update"
        />
        {call && itemRecord ? (
          <GridWrapper>
            <ExpressionEditor
              // @ts-expect-error - TS2322 - Type 'ContextualExpression | undefined' is not assignable to type 'ContextualExpression'.
              expression={itemRecord['Item ID']}
              dispatch={dispatchItemID(dispatch)}
            />
            <ExpressionEditor
              // @ts-expect-error - TS2322 - Type 'ContextualExpression | undefined' is not assignable to type 'ContextualExpression'.
              expression={itemRecord['Item Status']}
              dispatch={dispatchItemStatus(dispatch)}
            />
            <StyledLabel label="Fields to update">
              <StyledTooltipControl
                icon={<StyledHelpIcon />}
                aria-hidden
                content={
                  <TooltipContent>
                    Select the fields you want to update. Any unselected fields
                    will remain unchanged.
                  </TooltipContent>
                }
                alternate
              />
            </StyledLabel>
            <EnhancedDropdownWithNote
              button={
                <SelectButton
                  indicator
                  data-automation-id="select-field"
                >{`Select fields ${
                  selectedSlugs.length > 0
                    ? `(${selectedSlugs.length} selected)`
                    : ''
                }`}</SelectButton>
              }
              width={ENHANCED_DROPDOWN_WIDTH}
              dropdownData={dropdownList}
              handleChange={handleFieldSelect}
            />
            {selectedSlugs.length > 0 && (
              <>
                <Note colorVariant="default" hasIcon={false}>
                  {noteText}
                </Note>{' '}
                <Divider />
              </>
            )}

            {/** If there's basic fields that were selected for updating
             * we'll render the section heading for them and each field */}
            {selectedBasicFields.length > 0 && (
              <>
                <Subheading>Basic info</Subheading>
                {selectedFieldsExprList.map((item, idx) => {
                  const {val, tag} = getUnionData(item);
                  if (val && (tag === 'name' || tag === 'slug')) {
                    const label = getFieldBySlug({
                      fieldSlug: tag,
                      collection: selectedCollection,
                    }).name;
                    return (
                      <div key={tag}>
                        <LogicLabel label={label} />
                        <ExpressionEditor
                          expression={val}
                          dispatch={dispatchSelectedFields({
                            listIndex: idx,
                            tag,
                          })(dispatch)}
                        />
                      </div>
                    );
                  }
                })}
              </>
            )}

            {/** We only need to render the divider when we have both
             * basic fields, and custom fields, selected */}
            {selectedBasicFields.length > 0 &&
              selectedCustomFields.length > 0 && <Divider />}

            {/** If there's custom fields that were selected for updating
             * we'll render the section heading for them and each field */}
            {selectedCustomFields.length > 0 && (
              <>
                <Subheading>Custom fields</Subheading>
                {selectedFieldsExprList.map((item, idx) => {
                  const {val, tag} = getUnionData(item);
                  if (val && tag && tag !== 'name' && tag !== 'slug') {
                    return (
                      <ExpressionEditor
                        key={tag}
                        expression={val}
                        dispatch={dispatchSelectedFields({listIndex: idx, tag})(
                          dispatch
                        )}
                      />
                    );
                  }
                })}
              </>
            )}
          </GridWrapper>
        ) : null}
      </>
    );
  },
});

const GridWrapper = emotionStyled('div')({
  display: 'grid',
  gridRowGap: tokens.space[2],
});

const Divider = styledDiv(({theme: {gutters, colors}}) => ({
  height: 1,
  backgroundColor: colors.backgroundDeep,
  width: `calc(100% + ${gutters.x8 * 2 + gutters.x4}px)`,
  marginLeft: -gutters.x8 - gutters.x4 / 2,
}));

const StyledLabel = styled(LogicLabel)(({theme: {gutters}}) => ({
  display: 'grid',
  gridTemplateColumns: `max-content ${gutters.x16}px`,
  gridColumnGap: gutters.x4,
  alignItems: 'start',

  overflowY: 'visible',
  maxHeight: `${gutters.x16}px`,
  marginBottom: gutters.x4,
}));

const StyledTooltipControl = styled(TooltipControl)(({theme: {gutters}}) => ({
  marginLeft: gutters.x4,
  justifySelf: 'center',
  marginTop: '-2px',
}));

const TooltipContent = styledDiv(({theme: {gutters}}) => ({
  maxWidth: gutters.x4 * 56, // 224
}));

const StyledOption = emotionStyled('div')({
  display: 'flex',
  alignItems: 'center',
  columnGap: tokens.space[1],
});

const Space = emotionStyled('div')({
  width: tokens.space[5],
});

const StyledCheckmark = styled(Checkmark)(({theme: {gutters}}) => ({
  width: gutters.x20,
}));
